import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const PASSWORD = 'CrunchyMunchy';
const POLL_INTERVAL = 2000;

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

// Generate or retrieve anonymous user ID
function getUserId() {
  let userId = localStorage.getItem('things_userId');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('things_userId', userId);
  }
  return userId;
}

// Check if user is authenticated
function isAuthenticated() {
  return localStorage.getItem('things_auth') === 'true';
}

// Set authentication
function setAuthenticated() {
  localStorage.setItem('things_auth', 'true');
}

// Sanitize input
function sanitizeInput(text) {
  if (typeof text !== 'string') return '';
  return text.trim().slice(0, 2000);
}

// Default round data
const DEFAULT_ROUND_DATA = {
  currentRound: 1,
  rounds: { 1: { startTime: 0 } }
};

// Fetch round data from Supabase
async function fetchRoundData() {
  try {
    const { data, error } = await supabase
      .from('game_state')
      .select('*')
      .eq('id', 1)
      .single();

    if (error) {
      console.error('Failed to fetch round data:', error);
      return DEFAULT_ROUND_DATA;
    }

    return {
      currentRound: data.current_round,
      rounds: data.rounds
    };
  } catch (e) {
    console.error('Failed to fetch round data:', e);
    return DEFAULT_ROUND_DATA;
  }
}

// Save round data to Supabase
async function saveRoundData(newCurrentRound, newRounds) {
  try {
    const { error } = await supabase
      .from('game_state')
      .upsert({
        id: 1,
        current_round: newCurrentRound,
        rounds: newRounds
      });

    if (error) {
      console.error('Failed to save round data:', error);
      return false;
    }
    return true;
  } catch (e) {
    console.error('Failed to save round data:', e);
    return false;
  }
}

// Password Gate Component
function PasswordGate({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === PASSWORD) {
      setAuthenticated();
      onSuccess();
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  return (
    <div className="password-gate">
      <div className="password-modal">
        <div className="password-icon">🎅</div>
        <h2>Santa's Workshop</h2>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Secret Phrase"
            className="password-input"
          />
          {error && <p className="password-error">That's not the magic words!</p>}
          <button type="submit" className="password-button">
            🎄 Enter 🎄
          </button>
        </form>
      </div>
    </div>
  );
}

// Message Bubble Component
function MessageBubble({ message, isSent, showTail, isLast }) {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`message-wrapper ${isSent ? 'sent' : 'received'} ${showTail ? 'show-tail' : ''}`}>
      <div className={`message-bubble ${isSent ? 'sent' : 'received'}`}>
        <p className="message-text">{message.text}</p>
        <span className="message-time">{formatTime(message.timestamp)}</span>
      </div>
      {isSent && isLast && <div className="message-status">Delivered by reindeer ✨</div>}
    </div>
  );
}

// Game Popover Component
function GamePopover({ isOpen, onClose, currentRound, onNextRound, onPreviousRound, onNewGame }) {
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="game-popover" ref={popoverRef}>
      <div className="game-popover-header">Game Controls</div>
      <button className="game-popover-btn" onClick={onNextRound}>
        <span className="game-popover-icon">▶</span>
        Start Round {currentRound + 1}
      </button>
      <button 
        className="game-popover-btn" 
        onClick={onPreviousRound}
        disabled={currentRound <= 1}
      >
        <span className="game-popover-icon">◀</span>
        Go to Round {Math.max(1, currentRound - 1)}
      </button>
      <div className="game-popover-divider" />
      <button className="game-popover-btn game-popover-btn-danger" onClick={onNewGame}>
        <span className="game-popover-icon">↺</span>
        New Game
      </button>
    </div>
  );
}

// Chat Component
function Chat() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [roundData, setRoundData] = useState(DEFAULT_ROUND_DATA);
  const [showGamePopover, setShowGamePopover] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const userId = useRef(getUserId());
  const prevMessageCount = useRef(0);
  const userScrolledUp = useRef(false);
  const messagesContainerRef = useRef(null);

  const currentRound = roundData.currentRound;
  const currentRoundStartTime = roundData.rounds[currentRound]?.startTime || 0;

  const scrollToBottom = useCallback(() => {
    if (!userScrolledUp.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Track if user has scrolled up
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      userScrolledUp.current = !isAtBottom;
    }
  }, []);

  // Fetch both messages and round data
  const fetchData = useCallback(async () => {
    try {
      // Fetch messages and round data in parallel
      const [messagesResult, roundResult] = await Promise.all([
        supabase
          .from('messages')
          .select('*')
          .order('timestamp', { ascending: true })
          .limit(500),
        fetchRoundData()
      ]);

      if (messagesResult.error) throw messagesResult.error;

      // Map to frontend format
      const formattedMessages = (messagesResult.data || []).map(msg => ({
        id: msg.id,
        text: msg.text,
        senderId: msg.sender_id,
        timestamp: msg.timestamp
      }));

      setMessages(formattedMessages);
      setRoundData(roundResult);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter messages for current round
  const filteredMessages = messages.filter(msg => msg.timestamp >= currentRoundStartTime);

  // Scroll to bottom only when NEW messages arrive
  useEffect(() => {
    if (filteredMessages.length > prevMessageCount.current) {
      scrollToBottom();
    }
    prevMessageCount.current = filteredMessages.length;
  }, [filteredMessages.length, scrollToBottom]);

  // Game control handlers
  const handleNextRound = useCallback(async () => {
    const newRound = currentRound + 1;
    const newRounds = {
      ...roundData.rounds,
      [newRound]: { startTime: Date.now() }
    };
    
    // Optimistically update UI
    setRoundData({ currentRound: newRound, rounds: newRounds });
    setShowGamePopover(false);
    prevMessageCount.current = 0;
    userScrolledUp.current = false;
    
    // Save to Supabase
    await saveRoundData(newRound, newRounds);
  }, [currentRound, roundData.rounds]);

  const handlePreviousRound = useCallback(async () => {
    if (currentRound <= 1) return;
    const newRound = currentRound - 1;
    
    // Optimistically update UI
    setRoundData({ ...roundData, currentRound: newRound });
    setShowGamePopover(false);
    prevMessageCount.current = 0;
    userScrolledUp.current = false;
    
    // Save to Supabase
    await saveRoundData(newRound, roundData.rounds);
  }, [currentRound, roundData]);

  const handleNewGame = useCallback(async () => {
    const newRounds = { 1: { startTime: Date.now() } };
    
    // Optimistically update UI
    setRoundData({ currentRound: 1, rounds: newRounds });
    setShowGamePopover(false);
    prevMessageCount.current = 0;
    userScrolledUp.current = false;
    
    // Save to Supabase
    await saveRoundData(1, newRounds);
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = sanitizeInput(inputText);
    if (!text || isSending) return;

    setIsSending(true);
    setInputText('');

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          text: text,
          sender_id: userId.current,
          timestamp: Date.now()
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local messages
      const newMessage = {
        id: data.id,
        text: data.text,
        senderId: data.sender_id,
        timestamp: data.timestamp
      };
      setMessages((prev) => [...prev, newMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      setInputText(text); // Restore text on error
    } finally {
      setIsSending(false);
      inputRef.current?.focus();
    }
  };

  // Snowflakes for loading state too
  const loadingSnowflakes = Array.from({ length: 20 }, (_, i) => (
    <span key={i} className="snowflake">❄</span>
  ));

  if (isLoading) {
    return (
      <div className="chat-container">
        <div className="snow-container">{loadingSnowflakes}</div>
        <div className="chat-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Snowflakes for the festive effect
  const snowflakes = Array.from({ length: 20 }, (_, i) => (
    <span key={i} className="snowflake">❄</span>
  ));

  return (
    <div className="chat-container">
      <div className="snow-container">{snowflakes}</div>
      <div className="chat-header">
        <h1>Things</h1>
        <span className="chat-subtitle">Round {currentRound}</span>
        <div className="game-button-wrapper">
          <button 
            className="game-button"
            onClick={() => setShowGamePopover(!showGamePopover)}
          >
            🎁
          </button>
          <GamePopover
            isOpen={showGamePopover}
            onClose={() => setShowGamePopover(false)}
            currentRound={currentRound}
            onNextRound={handleNextRound}
            onPreviousRound={handlePreviousRound}
            onNewGame={handleNewGame}
          />
        </div>
      </div>

      <div 
        className="messages-container" 
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {filteredMessages.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet</p>
            <p className="no-messages-hint">Be the first to say something!</p>
          </div>
        ) : (
          filteredMessages.map((message, i) => {
            const nextMsg = filteredMessages[i + 1];
            // Tail only on the last message in a group from the same sender
            const isLastInGroup = !nextMsg || nextMsg.senderId !== message.senderId;
            const isOverallLast = i === filteredMessages.length - 1;
            return (
              <MessageBubble
                key={message.id}
                message={message}
                isSent={message.senderId === userId.current}
                showTail={isLastInGroup}
                isLast={isOverallLast}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="input-container" onSubmit={sendMessage}>
        <input
          ref={inputRef}
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Send a message to Santa's workshop..."
          className="message-input"
          disabled={isSending}
        />
        <button
          type="submit"
          className="send-button"
          disabled={!inputText.trim() || isSending}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <line x1="12" y1="19" x2="12" y2="5"></line>
            <polyline points="5 12 12 5 19 12"></polyline>
          </svg>
        </button>
      </form>
    </div>
  );
}

// Main Things Page Component
export default function Things() {
  const [authenticated, setAuthenticatedState] = useState(isAuthenticated());

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticatedState(true)} />;
  }

  return <Chat />;
}
